"use client";

import React, { useState, useEffect } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import IconCross from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCross.js";
import IconSunLmDe from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSunLmDe.js";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";

const FilterWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
`;

const StarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledIconCross = styled(IconCross)`
  cursor: pointer;
`;

const StarIcon = styled(IconSunLmDe)<{ $active: boolean; $hovered: boolean }>`
  color: ${({ $active, $hovered, theme }) =>
    $active || $hovered
      ? theme.kallisto.color.background.interactive.backgroundPrimaryDefault
      : theme.kallisto.color.background.interactive.backgroundTertiaryDefault};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) =>
      theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
  }
`;

interface StarsFilterProps {
  delayedUpdate?: boolean;
}

const StarsFilter: React.FC<StarsFilterProps> = ({ delayedUpdate = false }) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();

  const [starRating, setStarRating] = useState(() => {
    return delayedUpdate
      ? Number(tempFilters.get("stars")) || 0
      : Number(searchParams.get("stars")) || 0;
  });

  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  useEffect(() => {
    setStarRating(Number(searchParams.get("stars")) || 0);
  }, [searchParams]);

  const handleStarRatingChange = (stars: number) => {
    setStarRating(stars);

    if (delayedUpdate) {
      // Just update temp filters, will be committed on dialog confirmation
      updateTempFilter("stars", stars > 0 ? stars.toString() : null);
    } else {
      // Immediate URL update
      updateFilter("stars", stars > 0 ? stars.toString() : null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, stars: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleStarRatingChange(stars);
    }
  };

  return (
    <FilterWrapper data-testid="starsFilter">
      <span className="sr-only" id="star-rating-label">
        Filter by star rating
      </span>
      <StarWrapper role="radiogroup" aria-labelledby="star-rating-label">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            size={32}
            key={star}
            $active={star <= starRating}
            $hovered={hoveredStar !== null && star <= hoveredStar}
            onClick={() => handleStarRatingChange(star)}
            onKeyDown={(e) => handleKeyDown(e, star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(null)}
            role="radio"
            aria-checked={star === starRating}
            aria-label={`${star} star${star !== 1 ? "s" : ""}`}
            tabIndex={0}
          />
        ))}
      </StarWrapper>
      <StyledIconCross
        size={14}
        onClick={() => handleStarRatingChange(0)}
        onKeyDown={(e) => handleKeyDown(e, 0)}
        role="button"
        aria-label="Clear star rating filter"
        tabIndex={0}
      />
    </FilterWrapper>
  );
};

export default StarsFilter;
